// src/pages/program/TestOutPlayerPage.jsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getPhaseData } from '../../data/programData';
import { getTestOutExercises } from '../../data/testOutExercises';
import { getWarmupData } from '../../data/trainingSessionHelpers';
import { calculateTestOutResults } from '../../data/testOutScoring';
import { ArrowLeft, X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui';
import { WarmupPlayer } from '../../components/training/WarmupPlayer';
import { CardioWarmup } from '../../components/testout/CardioWarmup';
import { TestOutExercisePlayer } from '../../components/testout/TestOutExercisePlayer';
import { TestOutComplete } from '../../components/testout/TestOutComplete';

const TEST_OUT_STORAGE_KEY = 'acl_care_test_out_progress';

export function TestOutPlayerPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare, updateAclCare, user } = useAuth();

  const [sessionState, setSessionState] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [error, setError] = useState(null);

  const phaseNum = parseInt(phaseId);
  const phaseData = getPhaseData(phaseNum);

  // Get exercises based on session's exercise type
  const exercises = sessionState
    ? getTestOutExercises(phaseNum, sessionState.exerciseType)
    : [];

  // Get warmup data for movement warmup (test out variant)
  const warmupData = getWarmupData(phaseNum, true); // true = test out warmup

  // Load session on mount
  useEffect(() => {
    const stored = localStorage.getItem(TEST_OUT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.phaseNum === phaseNum) {
          setSessionState(parsed);
        } else {
          setError('Session mismatch. Please start a new test out.');
        }
      } catch (e) {
        console.error('Error parsing stored test out session:', e);
        setError('Failed to load session data');
      }
    } else {
      setError('No session found. Please start from the overview.');
    }
  }, [phaseNum]);

  const saveSessionState = (updates) => {
    const newState = { ...sessionState, ...updates };
    setSessionState(newState);
    localStorage.setItem(TEST_OUT_STORAGE_KEY, JSON.stringify(newState));
  };

  // Warmup handlers
  const handleWarmupExerciseComplete = (categoryIndex, exerciseIndex) => {
    saveSessionState({
      currentWarmupCategoryIndex: categoryIndex,
      currentWarmupExerciseIndex: exerciseIndex,
    });
  };

  const handleWarmupSkipCategory = (categoryIndex, exerciseIndex) => {
    saveSessionState({
      currentWarmupCategoryIndex: categoryIndex,
      currentWarmupExerciseIndex: exerciseIndex,
    });
  };

  const handleWarmupComplete = () => {
    saveSessionState({
      warmupCompleted: true,
      phase: 'exercises',
      currentExerciseIndex: 0,
    });
  };

  const handleCardioComplete = () => {
    saveSessionState({
      warmupCompleted: true,
      phase: 'exercises',
      currentExerciseIndex: 0,
    });
  };

  // Exercise handlers
  const handleExerciseComplete = (exerciseId, data) => {
    const newExerciseData = {
      ...(sessionState.exerciseData || {}),
      [exerciseId]: data,
    };

    const nextIndex = sessionState.currentExerciseIndex + 1;

    if (nextIndex >= exercises.length) {
      // All exercises complete
      saveSessionState({
        exerciseData: newExerciseData,
        phase: 'complete',
        completed: true,
        completedAt: new Date().toISOString(),
      });
    } else {
      saveSessionState({
        exerciseData: newExerciseData,
        currentExerciseIndex: nextIndex,
      });
    }
  };

  const handleNavigateExercise = (index) => {
    if (index >= 0 && index < exercises.length) {
      saveSessionState({
        currentExerciseIndex: index,
      });
    }
  };

  // Build test out results
  const buildTestOutResults = () => {
    const injurySide = aclCare?.injury_side || 'left';
    return calculateTestOutResults(
      phaseNum,
      exercises,
      sessionState.exerciseData || {},
      injurySide
    );
  };

  // Build API payload
  const buildTestOutPayload = (results) => {
    const startTime = new Date(sessionState.startedAt);
    const endTime = sessionState.completedAt
      ? new Date(sessionState.completedAt)
      : new Date();
    const durationMinutes = Math.round((endTime - startTime) / 60000);

    // Build exercises data
    const exercisesData = {};
    exercises.forEach(exercise => {
      exercisesData[exercise.id] = {
        exercise_id: exercise.id,
        display_name: exercise.displayName,
        scoring_key: exercise.scoringKey,
        data: sessionState.exerciseData?.[exercise.id] || null,
      };
    });

    // Build scores summary
    const scores = {};
    Object.values(results.exerciseResults).forEach(result => {
      scores[result.scoringKey] = {
        score: result.score,
        passed: result.passed,
        threshold: result.threshold,
      };
    });

    return {
      user_id: user.id,
      organization_id: user.organization_id,
      phase: phaseNum,
      warmup_type: sessionState.warmupType,
      exercise_type: sessionState.exerciseType ? 'machine' : 'non-machine',
      started_at: sessionState.startedAt,
      completed_at: sessionState.completedAt || new Date().toISOString(),
      duration_minutes: durationMinutes,
      warmup_completed: sessionState.warmupCompleted,
      overall_passed: results.overallPassed,
      exercises: exercisesData,
      scores,
    };
  };

  // Session complete handler
  const handleTestOutFinish = async () => {
    try {
      const results = buildTestOutResults();
      const payload = buildTestOutPayload(results);

      // Create test out record
      const result = await api.createTestOut(payload);

      // Update user's acl_care - start with existing data
      const phaseKey = `phase_${phaseNum}`;
      const currentAttempts = aclCare?.phases?.[phaseKey]?.test_out_attempts || 0;

      // Build updated phases object, preserving all existing phases
      const updatedPhases = {
        ...(aclCare?.phases || {}),
        [phaseKey]: {
          ...(aclCare?.phases?.[phaseKey] || {}),
          test_out_attempts: currentAttempts + 1,
        },
      };

      // If passed, update pass status and initialize next phase
      if (results.overallPassed) {
        updatedPhases[phaseKey].test_out_passed = true;
        updatedPhases[phaseKey].test_out_passed_at = new Date().toISOString();

        // Initialize next phase if not phase 4
        if (phaseNum < 4) {
          const nextPhaseKey = `phase_${phaseNum + 1}`;
          const nextPhaseNum = phaseNum + 1;

          // Create or update next phase with proper initialization
          updatedPhases[nextPhaseKey] = {
            // Preserve any existing data for this phase (in case of retakes)
            ...(aclCare?.phases?.[nextPhaseKey] || {}),
            // Set/update unlock status
            unlocked: true,
            unlocked_at: aclCare?.phases?.[nextPhaseKey]?.unlocked_at || new Date().toISOString(),
            unlocked_method: aclCare?.phases?.[nextPhaseKey]?.unlocked_method || 'test_out',
            // Initialize completions if not present
            completions: aclCare?.phases?.[nextPhaseKey]?.completions || {
              session_1: 0,
              session_2: 0,
              optional: 0,
            },
            // Initialize target completions if not present
            target_completions: aclCare?.phases?.[nextPhaseKey]?.target_completions || {
              session_1: 8,
              session_2: 8,
              optional: 4,
            },
            // Initialize test out status if not present
            test_out_passed: aclCare?.phases?.[nextPhaseKey]?.test_out_passed || false,
            test_out_attempts: aclCare?.phases?.[nextPhaseKey]?.test_out_attempts || 0,
          };
        }
      }

      // Build the complete updated acl_care object
      const updatedAclCare = {
        ...aclCare,
        phases: updatedPhases,
        current_phase: results.overallPassed && phaseNum < 4
          ? phaseNum + 1
          : aclCare.current_phase,
        updated_at: new Date().toISOString(),
      };

      await updateAclCare(updatedAclCare);

      // Clear session storage
      localStorage.removeItem(TEST_OUT_STORAGE_KEY);

      // Navigate to calendar
      navigate('/calendar');
    } catch (err) {
      console.error('[TestOutPlayerPage] Error saving test out:', err);
      setError('Failed to save test out. Please try again.');
    }
  };

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    navigate(`/program/phase/${phaseId}/test-out`);
  };

  // Loading state
  if (!sessionState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading test out...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate(`/program/phase/${phaseId}/test-out`)}>
            Back to Test Out
          </Button>
        </div>
      </div>
    );
  }

  // Get header title based on phase
  const getHeaderTitle = () => {
    if (sessionState.phase === 'warmup') {
      return sessionState.warmupType === 'cardio' ? 'Cardio Warmup' : 'Movement Warmup';
    }
    if (sessionState.phase === 'exercises') {
      return `Exercise ${sessionState.currentExerciseIndex + 1}/${exercises.length}`;
    }
    if (sessionState.phase === 'complete') {
      return 'Results';
    }
    return 'Test Out';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button
          onClick={handleExit}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>

        <h1 className="font-semibold text-gray-900">
          {getHeaderTitle()}
        </h1>

        <button
          onClick={handleExit}
          className="p-2 -mr-2 rounded-full hover:bg-gray-100"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Movement Warmup */}
        {sessionState.phase === 'warmup' && sessionState.warmupType === 'movement' && warmupData && (
          <WarmupPlayer
            warmupData={warmupData}
            currentCategoryIndex={sessionState.currentWarmupCategoryIndex || 0}
            currentExerciseIndex={sessionState.currentWarmupExerciseIndex || 0}
            onExerciseComplete={handleWarmupExerciseComplete}
            onSkipCategory={handleWarmupSkipCategory}
            onWarmupComplete={handleWarmupComplete}
            onExit={handleExit}
          />
        )}

        {/* Cardio Warmup */}
        {sessionState.phase === 'warmup' && sessionState.warmupType === 'cardio' && (
          <CardioWarmup
            onComplete={handleCardioComplete}
            onExit={handleExit}
          />
        )}

        {/* Exercises */}
        {sessionState.phase === 'exercises' && (
          <TestOutExercisePlayer
            phaseNum={phaseNum}
            exercises={exercises}
            currentIndex={sessionState.currentExerciseIndex}
            exerciseData={sessionState.exerciseData || {}}
            injurySide={aclCare?.injury_side || 'left'}
            onExerciseComplete={handleExerciseComplete}
            onNavigate={handleNavigateExercise}
            onExit={handleExit}
          />
        )}

        {/* Complete */}
        {sessionState.phase === 'complete' && (
          <TestOutComplete
            phaseNum={phaseNum}
            exercises={exercises}
            exerciseData={sessionState.exerciseData || {}}
            injurySide={aclCare?.injury_side || 'left'}
            sessionState={sessionState}
            onFinish={handleTestOutFinish}
          />
        )}
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" />

          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Exit Test Out?
              </h2>
              <p className="text-gray-600 mb-6">
                Your progress will be saved and you can resume later today.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowExitConfirm(false)}
                  fullWidth
                >
                  Continue
                </Button>
                <Button
                  onClick={confirmExit}
                  fullWidth
                >
                  Exit & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}